import { css, cx } from '@linaria/core'
import { type FC, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '#app/store'
import { selectMarketByConfiguration } from '#app/store/slices/configuration/marketSlice'
import { Button } from '#components/Button'
import { Icon } from '#components/Icon'
import { IconButton } from '#components/IconButton'
import { Modal } from '#components/Modal'
import { Spinner } from '#components/Spinner'
import { InlineErrorDisplay } from '#features/ErrorHandling/ErrorDisplay'
import CloseIcon from '#icons/close-16.svg'
import InfoIcon from '#icons/info-circle-24.svg'
import ErrorIcon from '#icons/warning-filled.svg'
import { OverrideChangeTable } from '#pages/Configuration/ChangeBaseParameters/OverrideChangeTable'
import { OverridesGrid, type OverridesState } from '#pages/Configuration/ChangeBaseParameters/OverridesGrid'
import { AbortError, applyBaseParameterOverrides, modifyBaseParameters } from '#pages/Configuration/ChangeBaseParameters/utils'
import { useConfigurationDifference } from '#pages/Configuration/Comparison/useConfigurationDifference'
import type { TruckConfiguration } from '#store/slices/configurationSlice'
import { enrichConfiguration } from '#store/slices/enrichConfiguration'
import { initialFavoritesState } from '#store/slices/userData/favoritesSlice'
import { dialogClass } from '#styles/dialogClass'
import { dateToString } from '#utils/DateString'
import { testIds } from '#utils/testIds'

export const baseParamsDialogClass = css`
  > ._dialog {
    width: 800px;
    height: 600px;
    min-width: 800px;
    min-height: 300px;
    display: flex;
  }
`

type UiState =
  | {
      step: 'choose-overrides'
    }
  | {
      step: 'calculating-changes'
    }
  | {
      step: 'error'
      message: string
      error: unknown
      prevStep: 'choose-overrides' | 'preview-overrides'
    }
  | {
      step: 'preview-overrides'
    }
  | {
      step: 'applying-overrides'
    }
  | {
      step: 'compare-configuration'
    }

interface ChangeBaseParametersOverlayProps {
  setIsOpen: (isOpen: boolean) => void
  isOpen: boolean
  configuration: TruckConfiguration
  displayMode?: 'part-period' | 'full'
}

export const ChangeBaseParametersOverlay: FC<ChangeBaseParametersOverlayProps> = ({ setIsOpen, isOpen, configuration, displayMode }) => {
  const [uiState, setUiState] = useState<UiState>({
    step: 'choose-overrides',
  })

  const market = useAppSelector((state) => selectMarketByConfiguration(state, configuration))

  const { t } = useTranslation()

  const enrichment = useMemo(() => {
    return enrichConfiguration(
      configuration.productGroups,
      configuration.configuredFamilies,
      configuration.initialSelection,
      market,
      initialFavoritesState,
      'writable',
      configuration.step.system,
      configuration.apiConfiguration,
      configuration.optionalEquipment,
      configuration.tg3?.tg3ConversionInsightsLookup,
      { currentConfigurationVersion: configuration.version, compatibility: configuration.compatibility },
    )
  }, [configuration, market])

  const stepData: Partial<Record<UiState['step'], { note?: string; level?: 'warn' | 'info'; hasCancel?: boolean }>> = {
    'choose-overrides': {
      note: t('configuration.change-base-params.info.choose-overrides', 'Create a new configuration with the selected overrides'),
      hasCancel: true,
    },
    'preview-overrides': {
      note: t(
        'configuration.change-base-params.info.preview-overrides',
        'Applying the selected overrides will replace the current configuration and the entire change history',
      ),
      level: 'warn',
    },
    'calculating-changes': {
      hasCancel: true,
    },
    'applying-overrides': {
      hasCancel: true,
    },
  }

  const [updatedConfiguration, setUpdatedConfiguration] = useState<TruckConfiguration | undefined>(undefined)

  const [overrides, setOverrides] = useState<OverridesState>({
    overrides: {
      desiredDealerDate:
        displayMode === 'part-period'
          ? {
              override: true,
              value: dateToString(new Date()),
            }
          : undefined,
    },
    hasOverrides: false,
    effectiveConfiguration: {
      ...configuration?.apiConfiguration,
    },
  })

  const updatedConfigurationEnrichment = useMemo(() => {
    if (!updatedConfiguration) {
      return undefined
    }
    return enrichConfiguration(
      updatedConfiguration.productGroups,
      updatedConfiguration.configuredFamilies,
      updatedConfiguration.initialSelection,
      market,
      initialFavoritesState,
      'writable',
      updatedConfiguration.step.system,
      updatedConfiguration.apiConfiguration,
      updatedConfiguration.optionalEquipment,
      updatedConfiguration.tg3?.tg3ConversionInsightsLookup,
      { currentConfigurationVersion: configuration.version, compatibility: configuration.compatibility },
    )
  }, [updatedConfiguration, configuration.compatibility, configuration.version, market])

  // flip left and right for table
  const diff = useConfigurationDifference(updatedConfiguration, updatedConfigurationEnrichment, updatedConfiguration, enrichment, true)

  const abortControllerRef = useRef<AbortController>(null)

  const closeWithCancel = useCallback(() => {
    if (abortControllerRef.current) {
      if (window.confirm(t('configuration.change-base-params.message.cancel-changes', 'Are you sure you want to abort?'))) {
        abortControllerRef.current?.abort()
      }
      setIsOpen(false)
    } else {
      setIsOpen(false)
    }
  }, [setIsOpen, t])

  const previewOverrides = useCallback(async () => {
    if (abortControllerRef.current) {
      throw new Error('Still applying changes, not started')
    }

    setUiState({ step: 'calculating-changes' })
    try {
      abortControllerRef.current = new AbortController()
      const updatedConfiguration = await modifyBaseParameters(configuration, overrides.effectiveConfiguration, abortControllerRef.current?.signal)
      setUpdatedConfiguration(updatedConfiguration)
      setUiState({ step: 'preview-overrides' })
    } catch (error) {
      if (error instanceof AbortError) {
        setUiState({ step: 'choose-overrides' })
        return
      }
      setUiState({
        step: 'error',
        message: t('configuration.change-base-params.message.calculating-changes-failed', 'Failed to calculate changes'),
        error: error,
        prevStep: 'choose-overrides',
      })
    } finally {
      abortControllerRef.current = null
    }
  }, [t, configuration, overrides])

  const applyOverrides = useCallback(async () => {
    if (abortControllerRef.current) {
      throw new Error('Still applying changes, not started')
    }
    if (!updatedConfiguration) {
      throw new Error('Configuration was not found')
    }

    setUiState({ step: 'applying-overrides' })
    try {
      abortControllerRef.current = new AbortController()
      console.log('Applying changes:', updatedConfiguration)
      await applyBaseParameterOverrides(configuration, updatedConfiguration, abortControllerRef.current?.signal)
      abortControllerRef.current = null
      setIsOpen(false)
    } catch (error) {
      if (error instanceof AbortError) {
        setUiState({ step: 'preview-overrides' })
        return
      }
      setUiState({
        step: 'error',
        message: t('configuration.change-base-params.message.applying-changes-failed', 'Failed to apply changes'),
        error: error,
        prevStep: 'preview-overrides',
      })
    } finally {
      abortControllerRef.current = null
    }
  }, [t, configuration, updatedConfiguration, setIsOpen])

  return (
    <Modal
      open={isOpen}
      className={cx(dialogClass, baseParamsDialogClass)}
      contentClassName={cx(uiState.step === 'compare-configuration' && 'full-size')}
      lifted
      onOpenChange={() => {
        closeWithCancel()
      }}
      dismissOnOutside={false}
      data-testid={testIds.changeBaseParameters.overlay}
    >
      <header>
        <h2>{t('configuration.change-base-params.title', 'Change Base Parameters')}</h2>
        <IconButton
          iconSize="m"
          className="icon accent"
          iconUrl={CloseIcon}
          title={t('shared:common.button.close')}
          onClick={() => setIsOpen(false)}
        />
      </header>
      <main>
        {uiState.step === 'applying-overrides' && (
          <div className="_busy-message">
            <Spinner size="xl" subtitle={t('configuration.change-base-params.message.applying', 'Applying changes...')} />
          </div>
        )}

        {uiState.step === 'calculating-changes' && (
          <div className="_busy-message">
            <Spinner size="xl" subtitle={t('configuration.change-base-params.message.calculating-changes', 'Calculating changes...')} />
          </div>
        )}

        {uiState.step === 'error' && (
          <div className="_error-message">
            <InlineErrorDisplay
              error={uiState.error}
              title={uiState.message}
              kind="inline"
              actions={
                <Button
                  kind="primary"
                  onClick={() => {
                    setUiState({ step: uiState.prevStep } as UiState)
                  }}
                >
                  {t('shared:common.button.retry')}
                </Button>
              }
            />
          </div>
        )}

        {uiState.step === 'preview-overrides' || uiState.step === 'choose-overrides' ? (
          <OverridesGrid
            disabled={uiState.step === 'preview-overrides'}
            apiConfiguration={configuration.apiConfiguration}
            overrides={overrides}
            calculatedPartPeriod={uiState.step === 'preview-overrides' ? updatedConfiguration?.apiConfiguration.partPeriod : undefined}
            onOverridesChange={setOverrides}
            displayMode={displayMode}
          />
        ) : null}

        {uiState.step === 'compare-configuration' && (
          <OverrideChangeTable
            baseConfiguration={configuration}
            baseEnrichment={enrichment}
            comparisonConfiguration={updatedConfiguration}
            comparisonEnrichment={updatedConfigurationEnrichment}
            difference={diff}
          />
        )}
      </main>
      <footer>
        {stepData[uiState.step]?.note && (
          <span className={cx('footer-note', stepData[uiState.step]?.level)}>
            <Icon iconUrl={stepData[uiState.step]?.level === 'warn' ? ErrorIcon : InfoIcon} size="m" />
            {stepData[uiState.step]?.note}
          </span>
        )}

        {stepData[uiState.step]?.hasCancel && (
          <Button kind="secondary" onClick={closeWithCancel}>
            {t('shared:common.button.cancel')}
          </Button>
        )}

        {uiState.step === 'choose-overrides' && (
          <Button kind="accent" onClick={previewOverrides} disabled={!overrides.hasOverrides}>
            {t('configuration.change-base-params.button.preview', 'Preview')}
          </Button>
        )}

        {uiState.step === 'compare-configuration' && (
          <>
            <Button
              kind="secondary"
              onClick={() => {
                setUiState({ step: 'preview-overrides' })
              }}
            >
              {t('shared:common.button.back')}
            </Button>
            <Button kind="accent" onClick={applyOverrides}>
              {t('shared:common.button.apply')}
            </Button>
          </>
        )}

        {uiState.step === 'preview-overrides' && (
          <>
            <Button
              kind="secondary"
              onClick={() => {
                setUiState({ step: 'choose-overrides' })
              }}
            >
              {t('shared:common.button.back')}
            </Button>
            <Button
              kind="secondary"
              onClick={() => {
                setUiState({ step: 'compare-configuration' })
              }}
              disabled={diff.affectedFamilies.size === 0}
            >
              {t('configuration.change-base-params.button.review', 'Review {{count}} changes', { count: diff.affectedFamilies.size })}
            </Button>
            <Button kind="accent" onClick={applyOverrides}>
              {t('shared:common.button.apply')}
            </Button>
          </>
        )}
      </footer>
    </Modal>
  )
}
